<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import {
  getCustomizationAttachmentUrl,
  listCustomizationAttachments,
  listCustomizations,
  updateCustomization,
  type CustomizationAttachment,
  type CustomizationRequest,
} from "../api/operations";

type CustomizationStatus = "NEW" | "CONTACTED" | "DONE";
type CustomizationStatusFilter = "ALL" | CustomizationStatus;
type JsonRecord = Record<string, unknown>;

interface DetailField {
  label: string;
  value: string;
}

const statusOptions: Array<{ value: CustomizationStatusFilter; label: string }> = [
  { value: "ALL", label: "全部" },
  { value: "NEW", label: "待联系" },
  { value: "CONTACTED", label: "已联系" },
  { value: "DONE", label: "已完成" },
];

const statusLabels: Record<CustomizationStatus, string> = {
  NEW: "待联系",
  CONTACTED: "已联系",
  DONE: "已完成",
};

const sourceActionLabels: Record<string, string> = {
  CONSULT: "咨询",
  START_CUSTOMIZATION: "开始定制",
  GET_QUOTE: "获取报价",
};

const rows = ref<CustomizationRequest[]>([]);
const loading = ref(true);
const loadError = ref("");
const searchQuery = ref("");
const statusFilter = ref<CustomizationStatusFilter>("ALL");
const expandedId = ref<string | null>(null);
const actionBusyId = ref<string | null>(null);
const actionSuccess = ref("");
const actionError = ref("");
const remarkDrafts = ref<Record<string, string>>({});
const attachmentsByRequestId = ref<Record<string, CustomizationAttachment[]>>({});
const attachmentLoadingId = ref<string | null>(null);
const attachmentOpenId = ref<string | null>(null);
const attachmentError = ref("");

function isRecord(value: unknown): value is JsonRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function textFrom(value: unknown): string | null {
  if (typeof value === "string") {
    const text = value.trim();
    return text || null;
  }
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    const items = value.map(textFrom).filter((item): item is string => Boolean(item));
    return items.length ? items.join("、") : null;
  }
  return null;
}

function firstText(...values: unknown[]): string | null {
  for (const value of values) {
    const text = textFrom(value);
    if (text) return text;
  }
  return null;
}

function numberFrom(value: unknown): number | null {
  const number = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  return Number.isFinite(number) ? number : null;
}

function parseApiDate(value: string): Date {
  const normalized = value.trim().replace(" ", "T");
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalized);
  return new Date(hasTimezone ? normalized : `${normalized}Z`);
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const date = parseApiDate(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("zh-CN", {
    timeZone: "Asia/Shanghai",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function moneyFromCents(value: unknown): string | null {
  const cents = numberFrom(value);
  return cents == null ? null : `¥${(cents / 100).toFixed(2)}`;
}

function productName(row: CustomizationRequest): string {
  return firstText(row.productNameSnapshot, row.productName) ?? "未关联商品";
}

function statusText(status: string): string {
  return statusLabels[status as CustomizationStatus] ?? "未知状态";
}

function statusClass(status: string): string {
  return `customization-status--${status.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
}

function sourceActionText(sourceAction: string | null | undefined): string {
  return sourceActionLabels[sourceAction ?? ""] ?? "其他入口";
}

function messageFrom(errorValue: unknown, fallback: string): string {
  return errorValue instanceof Error ? errorValue.message : fallback;
}

function displayOptionValue(record: JsonRecord): string {
  const nested = isRecord(record.value) ? record.value : null;
  const type = firstText(record.type, nested?.type);
  const unit = firstText(record.unit, nested?.unit);

  if (type === "SINGLE_SELECT") {
    const name = firstText(nested?.valueName, record.optionName, nested?.valueCode, record.optionCode);
    const custom = firstText(nested?.customValue, nested?.customLength, nested?.customWidth);
    return [name, custom].filter(Boolean).join(" / ") || "已选择";
  }

  if (type === "MULTI_SELECT") {
    const names = firstText(record.optionNames, nested?.valueNames, record.optionCodes, nested?.valueCodes);
    return names ?? "已选择";
  }

  if (type === "NUMBER") {
    const value = firstText(nested?.value, record.value);
    return value ? `${value}${unit ? ` ${unit}` : ""}` : "—";
  }

  if (type === "TEXT") {
    const text = firstText(nested?.value, record.value);
    const dimensions = isRecord(nested?.dimensions) ? nested.dimensions : isRecord(record.dimensions) ? record.dimensions : null;
    const flatLength = dimensions ? firstText(dimensions.flatLength) : null;
    const flatWidth = dimensions ? firstText(dimensions.flatWidth) : null;
    const flat = flatLength && flatWidth ? `展开尺寸：${flatLength}×${flatWidth}mm` : null;
    return [text, flat].filter(Boolean).join("；") || "—";
  }

  return firstText(
    record.optionName,
    record.optionNames,
    nested?.valueName,
    nested?.valueNames,
    nested?.valueCode,
    nested?.valueCodes,
    nested?.value,
    record.value,
  ) ?? "已保存参数";
}

function numberOptionValue(record: JsonRecord | undefined): number {
  if (!record) return 0;
  const nested = isRecord(record.value) ? record.value : null;
  return numberFrom(nested?.value ?? record.value) ?? 0;
}

function selectedOptionFields(value: unknown): DetailField[] {
  if (!Array.isArray(value)) return [];

  const records = value.filter(isRecord);
  const byCode = new Map(
    records.flatMap((record) => {
      const code = firstText(record.groupCode);
      return code ? [[code, record] as const] : [];
    }),
  );
  const spotColorCount = Math.max(0, Math.round(numberOptionValue(byCode.get("SPOT_COLOR_COUNT"))));

  return records.reduce<DetailField[]>((fields, item, index) => {
    if (!isRecord(item)) return fields;
    const groupCode = firstText(item.groupCode);
    if (groupCode === "SPOT_COLOR_COUNT") return fields;
    const label = firstText(item.groupName, item.groupCode) ?? `参数 ${index + 1}`;
    if (groupCode === "PANTONE_CODE") {
      const pantone = displayOptionValue(item);
      if (spotColorCount > 0 && pantone !== "—") fields.push({ label: "潘通号", value: pantone });
      return fields;
    }
    const optionValue = groupCode === "PRINT" && spotColorCount > 0
      ? `${displayOptionValue(item)}+${spotColorCount}专色`
      : displayOptionValue(item);
    fields.push({ label, value: optionValue });
    return fields;
  }, []);
}

function referencePriceText(value: unknown): string {
  if (!isRecord(value)) return "—";

  const quantity = numberFrom(value.quantity);
  const priceFrom = moneyFromCents(value.priceFrom);
  const priceTo = moneyFromCents(value.priceTo);
  const price = priceFrom && priceTo ? `${priceFrom} - ${priceTo}` : priceFrom ?? priceTo;
  const parts = [
    quantity == null ? null : `${quantity.toLocaleString("zh-CN")} 个起`,
    price,
    firstText(value.pricingNote),
  ].filter((item): item is string => Boolean(item));

  return parts.join(" · ") || "—";
}

const filteredRows = computed(() => {
  const query = searchQuery.value.trim().toLocaleLowerCase();

  return rows.value.filter((row) => {
    const searchableText = [
      row.requestNo,
      productName(row),
      row.contactName,
      row.contactPhone,
      row.requirementDescription,
      statusText(row.status),
    ]
      .filter(Boolean)
      .join(" ")
      .toLocaleLowerCase();
    const matchesSearch = !query || searchableText.includes(query);
    const matchesStatus = statusFilter.value === "ALL" || row.status === statusFilter.value;
    return matchesSearch && matchesStatus;
  });
});

async function load(): Promise<boolean> {
  loading.value = true;
  loadError.value = "";
  try {
    rows.value = await listCustomizations();
    const drafts = { ...remarkDrafts.value };
    for (const row of rows.value) {
      if (!(row.id in drafts)) drafts[row.id] = row.adminRemark ?? "";
    }
    remarkDrafts.value = drafts;
    return true;
  } catch (errorValue) {
    loadError.value = messageFrom(errorValue, "定制咨询加载失败");
    return false;
  } finally {
    loading.value = false;
  }
}

async function toggleDetails(id: string): Promise<void> {
  attachmentError.value = "";
  if (expandedId.value === id) {
    expandedId.value = null;
    return;
  }
  expandedId.value = id;
  if (attachmentsByRequestId.value[id]) return;
  attachmentLoadingId.value = id;
  try {
    attachmentsByRequestId.value[id] = await listCustomizationAttachments(id);
  } catch (errorValue) {
    attachmentError.value = messageFrom(errorValue, "附件列表加载失败");
  } finally {
    attachmentLoadingId.value = null;
  }
}

function formatFileSize(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function isImageAttachment(attachment: CustomizationAttachment): boolean {
  return attachment.contentType.startsWith("image/");
}

async function openAttachment(requestId: string, attachment: CustomizationAttachment): Promise<void> {
  if (attachmentOpenId.value) return;
  attachmentOpenId.value = attachment.id;
  attachmentError.value = "";
  try {
    const result = await getCustomizationAttachmentUrl(requestId, attachment.id);
    window.open(result.url, "_blank", "noopener,noreferrer");
  } catch (errorValue) {
    attachmentError.value = messageFrom(errorValue, "附件打开失败，请稍后重试");
  } finally {
    attachmentOpenId.value = null;
  }
}

function clearFilters(): void {
  searchQuery.value = "";
  statusFilter.value = "ALL";
}

function nextStatus(row: CustomizationRequest): "CONTACTED" | "DONE" | null {
  if (row.status === "NEW") return "CONTACTED";
  if (row.status === "CONTACTED") return "DONE";
  return null;
}

function actionLabel(row: CustomizationRequest): string {
  if (row.status === "NEW") return "标记已联系";
  if (row.status === "CONTACTED") return "标记已完成";
  return "—";
}

async function updateStatus(row: CustomizationRequest): Promise<void> {
  const status = nextStatus(row);
  if (!status || actionBusyId.value) return;

  actionBusyId.value = row.id;
  actionSuccess.value = "";
  actionError.value = "";

  try {
    await updateCustomization(row.id, status, remarkDrafts.value[row.id] ?? row.adminRemark ?? null);
    const reloaded = await load();
    if (!reloaded) {
      actionError.value = "状态已更新，但列表刷新失败，请稍后重新加载。";
      return;
    }
    actionSuccess.value = `${row.requestNo}已更新为${statusText(status)}`;
  } catch (errorValue) {
    actionError.value = messageFrom(errorValue, "咨询状态更新失败，请稍后重试");
  } finally {
    actionBusyId.value = null;
  }
}

onMounted(() => {
  void load();
});
</script>

<template>
  <section class="customizations-page">
    <header class="customizations-header">
      <div>
        <h1>定制咨询</h1>
        <p class="customizations-intro">
          管理客户提交的定制需求和跟进状态；当前列表仅处理已加载的真实咨询记录。
        </p>
      </div>
      <span class="customizations-loaded">当前已加载 {{ rows.length }} 条咨询</span>
    </header>

    <section class="customizations-workspace">
      <div class="customizations-toolbar">
        <div>
          <h2>咨询列表</h2>
          <p>搜索和筛选仅作用于当前接口已返回的真实记录（最多 200 条）。</p>
        </div>
        <label class="customizations-control customizations-search">
          <span>搜索</span>
          <input
            v-model="searchQuery"
            aria-label="搜索当前定制咨询"
            placeholder="搜索咨询号、商品、联系人、电话或需求"
            type="search"
          >
        </label>
      </div>

      <div
        class="customizations-status-tabs"
        role="tablist"
        aria-label="咨询状态筛选"
      >
        <button
          v-for="option in statusOptions"
          :key="option.value"
          :class="{ 'customizations-status-tab--active': statusFilter === option.value }"
          :aria-selected="statusFilter === option.value"
          type="button"
          role="tab"
          @click="statusFilter = option.value"
        >
          {{ option.label }}
        </button>
      </div>

      <p
        v-if="actionSuccess"
        aria-live="polite"
        class="customizations-feedback customizations-feedback--success"
      >
        {{ actionSuccess }}
      </p>
      <p
        v-if="actionError"
        aria-live="assertive"
        class="customizations-feedback customizations-feedback--error"
      >
        {{ actionError }}
      </p>

      <div
        v-if="loading"
        class="customizations-feedback customizations-feedback--loading"
      >
        正在加载定制咨询…
      </div>
      <div
        v-else-if="loadError"
        aria-live="polite"
        class="customizations-feedback customizations-feedback--error"
      >
        {{ loadError }}
        <button
          type="button"
          @click="void load()"
        >
          重新加载
        </button>
      </div>
      <div
        v-else
        class="customizations-table-wrap"
      >
        <table class="customizations-table">
          <thead>
            <tr>
              <th scope="col">
                咨询号 / 商品
              </th>
              <th scope="col">
                联系人
              </th>
              <th scope="col">
                联系电话
              </th>
              <th scope="col">
                预计数量
              </th>
              <th scope="col">
                提交时间
              </th>
              <th scope="col">
                状态
              </th>
              <th scope="col">
                操作
              </th>
            </tr>
          </thead>
          <tbody>
            <template
              v-for="row in filteredRows"
              :key="row.id"
            >
              <tr :class="{ 'customizations-row--expanded': expandedId === row.id }">
                <td class="customizations-identity-cell">
                  <strong>{{ row.requestNo }}</strong>
                  <span :title="productName(row)">{{ productName(row) }}</span>
                </td>
                <td>{{ row.contactName || "—" }}</td>
                <td>{{ row.contactPhone || "—" }}</td>
                <td class="customizations-quantity">
                  {{ row.quantity == null ? "—" : `${row.quantity.toLocaleString("zh-CN")} 个` }}
                </td>
                <td>
                  <time :datetime="row.createdAt">{{ formatDate(row.createdAt) }}</time>
                </td>
                <td>
                  <span
                    class="customizations-status"
                    :class="statusClass(row.status)"
                  >
                    {{ statusText(row.status) }}
                  </span>
                </td>
                <td class="customizations-actions">
                  <button
                    class="customizations-detail-button"
                    :aria-expanded="expandedId === row.id"
                    type="button"
                    @click="void toggleDetails(row.id)"
                  >
                    {{ expandedId === row.id ? "收起详情" : "查看详情" }}
                  </button>
                  <button
                    v-if="nextStatus(row)"
                    :disabled="actionBusyId !== null"
                    type="button"
                    @click="void updateStatus(row)"
                  >
                    {{ actionBusyId === row.id ? "更新中…" : actionLabel(row) }}
                  </button>
                  <span
                    v-else
                    class="customizations-no-action"
                  >{{ actionLabel(row) }}</span>
                </td>
              </tr>
              <tr
                v-if="expandedId === row.id"
                class="customizations-detail-row"
              >
                <td colspan="7">
                  <div class="customizations-detail">
                    <div class="customizations-detail-heading">
                      <div>
                        <h3>咨询详情</h3>
                        <p>{{ productName(row) }}</p>
                      </div>
                      <span class="customizations-detail-source">来源：{{ sourceActionText(row.sourceAction) }}</span>
                    </div>

                    <dl class="customizations-detail-grid">
                      <div>
                        <dt>咨询号</dt>
                        <dd>{{ row.requestNo }}</dd>
                      </div>
                      <div>
                        <dt>联系人</dt>
                        <dd>{{ row.contactName || "—" }}</dd>
                      </div>
                      <div>
                        <dt>联系电话</dt>
                        <dd>{{ row.contactPhone || "—" }}</dd>
                      </div>
                      <div>
                        <dt>提交时间</dt>
                        <dd>{{ formatDate(row.createdAt) }}</dd>
                      </div>
                      <div>
                        <dt>预计数量</dt>
                        <dd>{{ row.quantity == null ? "—" : `${row.quantity.toLocaleString("zh-CN")} 个` }}</dd>
                      </div>
                      <div>
                        <dt>参考价格</dt>
                        <dd>{{ referencePriceText(row.referencePriceSnapshot) }}</dd>
                      </div>
                      <div class="customizations-detail-grid__wide">
                        <dt>需求描述</dt>
                        <dd class="customizations-detail-copy">
                          {{ row.requirementDescription || "—" }}
                        </dd>
                      </div>
                    </dl>

                    <div class="customizations-detail-columns">
                      <section class="customizations-detail-panel">
                        <h4>定制参数</h4>
                        <dl
                          v-if="selectedOptionFields(row.selectedOptions).length"
                          class="customizations-options-list"
                        >
                          <div
                            v-for="field in selectedOptionFields(row.selectedOptions)"
                            :key="`${field.label}-${field.value}`"
                          >
                            <dt>{{ field.label }}</dt>
                            <dd>{{ field.value }}</dd>
                          </div>
                        </dl>
                        <p
                          v-else
                          class="customizations-detail-muted"
                        >
                          未保存定制参数
                        </p>
                      </section>

                      <section class="customizations-detail-panel">
                        <h4>跟进记录</h4>
                        <dl class="customizations-follow-up">
                          <div>
                            <dt>当前状态</dt>
                            <dd>{{ statusText(row.status) }}</dd>
                          </div>
                          <div>
                            <dt>联系时间</dt>
                            <dd>{{ formatDate(row.contactedAt) }}</dd>
                          </div>
                          <div>
                            <dt>完成时间</dt>
                            <dd>{{ formatDate(row.completedAt) }}</dd>
                          </div>
                          <div>
                            <dt>管理员备注</dt>
                            <dd
                              v-if="row.status === 'DONE'"
                              class="customizations-detail-copy"
                            >
                              {{ row.adminRemark || "—" }}
                            </dd>
                            <textarea
                              v-else
                              v-model="remarkDrafts[row.id]"
                              maxlength="500"
                              placeholder="可选；将在下一次状态更新时保存"
                              rows="3"
                            />
                          </div>
                        </dl>
                        <p
                          v-if="row.status !== 'DONE'"
                          class="customizations-detail-hint"
                        >
                          备注会随“{{ actionLabel(row) }}”一起提交，当前没有单独保存备注的接口。
                        </p>
                      </section>
                    </div>

                    <section class="customizations-detail-panel customizations-attachments-panel">
                      <div class="customizations-attachments-heading">
                        <h4>附件</h4>
                        <span
                          v-if="attachmentLoadingId === row.id"
                          class="customizations-detail-muted"
                        >正在加载</span>
                      </div>
                      <p
                        v-if="attachmentError"
                        class="customizations-detail-hint customizations-attachments-error"
                      >
                        {{ attachmentError }}
                      </p>
                      <p
                        v-else-if="!attachmentsByRequestId[row.id]?.length"
                        class="customizations-detail-muted"
                      >
                        未上传附件
                      </p>
                      <div
                        v-else
                        class="customizations-attachments-list"
                      >
                        <div
                          v-for="attachment in attachmentsByRequestId[row.id]"
                          :key="attachment.id"
                          class="customizations-attachment-item"
                        >
                          <span class="customizations-attachment-type">
                            {{ isImageAttachment(attachment) ? "图片" : "PDF" }}
                          </span>
                          <div class="customizations-attachment-info">
                            <strong>{{ attachment.originalFilename }}</strong>
                            <span>{{ formatFileSize(attachment.fileSize) }} · {{ formatDate(attachment.uploadedAt) }}</span>
                          </div>
                          <button
                            type="button"
                            :disabled="attachmentOpenId === attachment.id"
                            @click="void openAttachment(row.id, attachment)"
                          >
                            {{ attachmentOpenId === attachment.id ? "生成链接中" : "查看" }}
                          </button>
                        </div>
                      </div>
                    </section>
                  </div>
                </td>
              </tr>
            </template>
            <tr v-if="!filteredRows.length">
              <td
                colspan="7"
                class="customizations-empty"
              >
                <strong>{{ rows.length ? "没有匹配的定制咨询" : "当前没有定制咨询" }}</strong>
                <p>{{ rows.length ? "请调整搜索词或状态筛选。" : "当前接口没有返回定制咨询记录。" }}</p>
                <button
                  v-if="searchQuery || statusFilter !== 'ALL'"
                  type="button"
                  @click="clearFilters"
                >
                  清除筛选
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <footer class="customizations-table-footer">
          <span>显示 {{ filteredRows.length }} / {{ rows.length }} 条咨询</span>
          <span>状态流转：待联系 → 已联系 → 已完成</span>
        </footer>
      </div>
    </section>
  </section>
</template>

<style scoped>
.customizations-page {
  display: grid;
  gap: 24px;
  width: calc(100% - 48px);
  max-width: 1440px;
  margin: 0 auto;
  padding: 36px 0 136px;
  color: var(--admin-ink);
}

.customizations-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 28px;
}

.customizations-header h1 {
  margin: 0 0 8px;
  font-size: clamp(32px, 3vw, 42px);
  letter-spacing: -0.04em;
  line-height: 1.1;
}

.customizations-intro,
.customizations-toolbar p {
  margin: 0;
  color: var(--admin-muted);
  font-size: 14px;
  line-height: 1.65;
}

.customizations-loaded {
  padding-bottom: 4px;
  color: var(--admin-muted);
  font-size: 13px;
  white-space: nowrap;
}

.customizations-workspace {
  overflow: hidden;
  border: 1px solid var(--admin-outline-soft);
  border-radius: 16px;
  background: var(--admin-surface);
  box-shadow: 0 4px 20px rgba(28, 67, 46, 0.04);
}

.customizations-toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  padding: 24px 28px 20px;
  border-bottom: 1px solid var(--admin-outline-soft);
}

.customizations-toolbar h2 {
  margin: 0;
  font-size: 22px;
  letter-spacing: -0.03em;
}

.customizations-toolbar p {
  margin-top: 6px;
  font-size: 13px;
}

.customizations-control {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--admin-muted);
  font-size: 13px;
  font-weight: 700;
}

.customizations-search input {
  width: 310px;
  min-height: 42px;
  padding: 9px 12px;
  border: 1px solid var(--admin-outline);
  border-radius: 9px;
  background: var(--admin-surface-low);
  color: var(--admin-ink);
}

.customizations-status-tabs {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 16px 28px;
  border-bottom: 1px solid var(--admin-outline-soft);
}

.customizations-status-tabs button,
.customizations-actions button,
.customizations-empty button,
.customizations-feedback button {
  min-height: 40px;
  padding: 9px 16px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: var(--admin-muted);
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}

.customizations-status-tabs button:hover,
.customizations-status-tab--active {
  border-color: rgba(60, 179, 113, 0.24) !important;
  background: var(--admin-primary-soft) !important;
  color: var(--admin-primary-deep) !important;
}

.customizations-table-wrap {
  overflow-x: auto;
}

.customizations-table {
  width: 100%;
  min-width: 1020px;
  border-collapse: collapse;
  text-align: left;
}

.customizations-table th,
.customizations-table td {
  padding: 16px 20px;
  border-bottom: 1px solid var(--admin-outline-soft);
  vertical-align: top;
}

.customizations-table th {
  background: var(--admin-surface-low);
  color: var(--admin-muted);
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.customizations-table td {
  color: var(--admin-ink);
  font-size: 14px;
}

.customizations-table tbody tr:not(.customizations-detail-row) {
  transition: background-color 160ms ease;
}

.customizations-table tbody tr:not(.customizations-detail-row):hover,
.customizations-row--expanded {
  background: #fbfdfb;
}

.customizations-identity-cell {
  min-width: 260px;
}

.customizations-identity-cell strong,
.customizations-identity-cell span {
  display: block;
}

.customizations-identity-cell strong {
  color: var(--admin-ink);
  font-size: 14px;
  line-height: 1.45;
}

.customizations-identity-cell span {
  overflow: hidden;
  margin-top: 5px;
  color: var(--admin-muted);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.customizations-quantity {
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.customizations-status {
  display: inline-flex;
  min-width: 68px;
  justify-content: center;
  padding: 5px 9px;
  border: 1px solid #d7ded9;
  border-radius: 7px;
  background: #f5f7f6;
  color: #53605a;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.35;
  white-space: nowrap;
}

.customizations-status--new {
  border-color: #f0d9ad;
  background: #fff8e8;
  color: #99630d;
}

.customizations-status--contacted {
  border-color: #b8dfc8;
  background: #f0faf4;
  color: #248a55;
}

.customizations-status--done {
  border-color: #c7d9ec;
  background: #f1f7ff;
  color: #356b9f;
}

.customizations-actions {
  min-width: 220px;
  white-space: nowrap;
}

.customizations-actions button:not(.customizations-detail-button) {
  background: var(--admin-primary);
  color: #fff;
}

.customizations-actions button:not(.customizations-detail-button):hover {
  background: var(--admin-primary-deep);
}

.customizations-detail-button {
  margin-right: 8px;
  border-color: var(--admin-outline-soft) !important;
  background: var(--admin-surface-low) !important;
  color: var(--admin-primary-deep) !important;
}

.customizations-no-action {
  color: var(--admin-muted);
  font-size: 13px;
}

.customizations-actions button:disabled,
.customizations-status-tabs button:disabled,
.customizations-empty button:disabled,
.customizations-feedback button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.customizations-feedback {
  margin: 20px 28px;
  padding: 14px 16px;
  border-radius: 10px;
  font-size: 14px;
  line-height: 1.5;
}

.customizations-feedback--loading {
  border: 1px dashed var(--admin-outline);
  color: var(--admin-muted);
  text-align: center;
}

.customizations-feedback--success {
  border: 1px solid #b8dfc8;
  background: var(--admin-primary-soft);
  color: var(--admin-primary-deep);
}

.customizations-feedback--error {
  border: 1px solid #e8caca;
  background: #fff5f5;
  color: #a34848;
}

.customizations-feedback button {
  margin-left: 12px;
  border-color: currentColor;
  color: inherit;
}

.customizations-detail-row td {
  padding: 0;
  background: #fbfdfb;
}

.customizations-detail {
  display: grid;
  gap: 20px;
  padding: 24px 28px 28px;
  border-bottom: 1px solid var(--admin-outline-soft);
}

.customizations-detail-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
}

.customizations-detail-heading h3 {
  margin: 0;
  font-size: 18px;
}

.customizations-detail-heading p,
.customizations-detail-source {
  margin: 5px 0 0;
  color: var(--admin-muted);
  font-size: 13px;
}

.customizations-detail-source {
  margin-top: 2px;
  white-space: nowrap;
}

.customizations-detail-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin: 0;
}

.customizations-detail-grid > div,
.customizations-detail-panel {
  min-width: 0;
  padding: 14px 16px;
  border: 1px solid var(--admin-outline-soft);
  border-radius: 10px;
  background: var(--admin-surface);
}

.customizations-detail-grid__wide {
  grid-column: 1 / -1;
}

.customizations-detail-grid dt,
.customizations-options-list dt,
.customizations-follow-up dt {
  color: var(--admin-muted);
  font-size: 12px;
}

.customizations-detail-grid dd,
.customizations-options-list dd,
.customizations-follow-up dd {
  margin: 7px 0 0;
  color: var(--admin-ink);
  font-size: 14px;
  line-height: 1.55;
}

.customizations-detail-copy {
  white-space: pre-wrap;
  word-break: break-word;
}

.customizations-detail-columns {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(300px, 0.9fr);
  gap: 20px;
}

.customizations-detail-panel h4 {
  margin: 0 0 14px;
  font-size: 15px;
}

.customizations-attachments-panel {
  display: grid;
  gap: 14px;
}

.customizations-attachments-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.customizations-attachments-heading h4 {
  margin: 0;
}

.customizations-attachments-list {
  display: grid;
  gap: 10px;
}

.customizations-attachment-item {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid var(--admin-outline-soft);
  border-radius: 8px;
  background: var(--admin-surface-low);
}

.customizations-attachment-type {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 30px;
  border-radius: 6px;
  background: var(--admin-primary-soft);
  color: var(--admin-primary-deep);
  font-size: 11px;
  font-weight: 700;
}

.customizations-attachment-info {
  display: grid;
  flex: 1;
  min-width: 0;
  gap: 3px;
}

.customizations-attachment-info strong,
.customizations-attachment-info span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.customizations-attachment-info strong {
  color: var(--admin-ink);
  font-size: 13px;
  font-weight: 600;
}

.customizations-attachment-info span {
  color: var(--admin-muted);
  font-size: 12px;
}

.customizations-attachment-item button {
  flex: 0 0 auto;
  padding: 6px 10px;
  border: 1px solid var(--admin-outline);
  border-radius: 7px;
  background: var(--admin-surface);
  color: var(--admin-primary-deep);
  font-size: 12px;
}

.customizations-attachment-item button:disabled {
  cursor: wait;
  opacity: 0.6;
}

.customizations-attachments-error {
  color: #a34848;
}

.customizations-options-list,
.customizations-follow-up {
  display: grid;
  gap: 12px;
  margin: 0;
}

.customizations-options-list > div,
.customizations-follow-up > div {
  display: grid;
  grid-template-columns: 84px minmax(0, 1fr);
  gap: 12px;
}

.customizations-options-list dd,
.customizations-follow-up dd {
  margin: 0;
}

.customizations-follow-up textarea {
  width: 100%;
  min-height: 74px;
  box-sizing: border-box;
  padding: 9px 10px;
  border: 1px solid var(--admin-outline);
  border-radius: 8px;
  background: var(--admin-surface-low);
  color: var(--admin-ink);
  font: inherit;
  line-height: 1.5;
  resize: vertical;
}

.customizations-detail-muted,
.customizations-detail-hint {
  margin: 0;
  color: var(--admin-muted);
  font-size: 13px;
  line-height: 1.55;
}

.customizations-detail-hint {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--admin-outline-soft);
}

.customizations-empty {
  padding: 48px 20px !important;
  color: var(--admin-muted) !important;
  text-align: center;
}

.customizations-empty strong {
  display: block;
  color: var(--admin-ink);
  font-size: 15px;
}

.customizations-empty p {
  margin: 8px 0 16px;
}

.customizations-empty button {
  border-color: rgba(60, 179, 113, 0.24);
  background: var(--admin-primary-soft);
  color: var(--admin-primary-deep);
}

.customizations-table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 16px 20px;
  color: var(--admin-muted);
  font-size: 12px;
}

button:focus-visible,
input:focus-visible,
textarea:focus-visible {
  outline: 3px solid rgba(60, 179, 113, 0.25);
  outline-offset: 2px;
}

@media (max-width: 900px) {
  .customizations-page {
    width: min(100% - 32px, 720px);
    padding-top: 22px;
  }

  .customizations-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .customizations-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .customizations-search input {
    flex: 1;
    width: auto;
  }

  .customizations-detail-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .customizations-detail-columns {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .customizations-page {
    width: min(100% - 24px, 560px);
    padding-bottom: 154px;
  }

  .customizations-toolbar,
  .customizations-status-tabs {
    padding-right: 18px;
    padding-left: 18px;
  }

  .customizations-table-footer,
  .customizations-detail-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .customizations-detail {
    padding: 20px 18px 24px;
  }

  .customizations-detail-grid {
    grid-template-columns: 1fr;
  }

  .customizations-detail-grid__wide {
    grid-column: auto;
  }

  .customizations-detail-source {
    white-space: normal;
  }
}
</style>
